import { Helmet } from 'react-helmet-async';
import { RouteObject } from 'react-router-dom';
import { Root } from './root';
import { ErrorPage } from '../error-page';
import { Home } from './home';
import { Pricing } from './pricing';
import { Contact } from './contact';
import { LoyaltyProgram } from './loyalty-program';
import { About } from './about';
import { ThankYou } from './thank-you';
import { Blog } from './blog';
import { BlogPost } from './blog/BlogPost';
import ScrollToTop from '../config/scrollToTop';

type Breadcrumb = {
    position: number;
    name: string;
    item: string;
};

type PageMeta = {
    title: string;
    description: string;
    breadcrumb: Breadcrumb[];
};

type PageConfig = {
    path: string;
    element: JSX.Element;
    meta: PageMeta;
};

const baseUrl = 'https://www.e-smartwash.co.nz';

const pages: PageConfig[] = [
    {
        path: '/',
        element: <Home />,
        meta: {
            title: 'Hamilton Laundromat | Self Service Laundry Services - E-SmartWash',
            description: 'Fast, affordable self service laundry services at our Hamilton laundromat. Visit E-SmartWash for easy, eco-friendly washing—no waiting around.',
            breadcrumb: [
                { position: 1, name: 'Home', item: `${baseUrl}/` },
            ],
        },
    },
    {
        path: '/pricing',
        element: <Pricing />,
        meta: {
            title: ' Pricing - E-SmartWash',
            description: 'Check out E-SmartWash pricing for self service laundry in Hamilton. Simple, affordable rates with no hidden fees—just clean clothes, fast.',
            breadcrumb: [
                { position: 1, name: 'Home', item: `${baseUrl}/` },
                { position: 2, name: 'Pricing', item: `${baseUrl}/pricing/` },
            ],
        },
    },
    {
        path: '/contact',
        element: <Contact />,
        meta: {
            title: 'Contact Us - E-SmartWash',
            description: 'Got questions or need help with our Hamilton laundromat? Reach out to E-SmartWash—we’re here to make laundry day smoother.',
            breadcrumb: [
                { position: 1, name: 'Home', item: `${baseUrl}/` },
                { position: 2, name: 'Contact Us', item: `${baseUrl}/contact/` },
            ],
        },
    },
    {
        path: '/about',
        element: <About />,
        meta: {
            title: 'About Us - E-SmartWash',
            description: 'Learn how E-SmartWash brings self service laundry to Hamilton with eco-friendly tech, fast service, and a no-fuss approach. We make washing easier.',
            breadcrumb: [
                { position: 1, name: 'Home', item: `${baseUrl}/` },
                { position: 2, name: 'About Us', item: `${baseUrl}/about/` },
            ],
        },
    },
    {
        path: '/loyalty-program',
        element: <LoyaltyProgram />,
        meta: {
            title: 'Loyalty Program - E-SmartWash',
            description: 'Join the E-SmartWash loyalty program and save on every wash. Earn rewards while keeping your laundry fresh at our Hamilton laundromat.',
            breadcrumb: [
                { position: 1, name: 'Home', item: `${baseUrl}/` },
                { position: 2, name: 'Loyalty Program', item: `${baseUrl}/loyalty-program/` },
            ],
        },
    },
    {
        path: '/thank-you',
        element: <ThankYou />,
        meta: {
            title: 'Thank You - E-SmartWash',
            description: '',
            breadcrumb: [
                { position: 1, name: 'Home', item: `${baseUrl}/` },
                { position: 2, name: 'Thank You', item: `${baseUrl}/thank-you/` },
            ],
        },
    },
    {
        path: '/blog',
        element: <Blog />,
        meta: {
            title: 'Blog - E-SmartWash',
            description: 'Get laundry tips, updates, and hacks from the E-SmartWash blog. Stay fresh, save time, and make laundry day less of a chore.',
            breadcrumb: [
                { position: 1, name: 'Home', item: `${baseUrl}/` },
                { position: 2, name: 'Blog', item: `${baseUrl}/blog/` },
            ],
        },
    },

    {
        path: '/blog/:slug',
        element: <BlogPost />,
        meta: {
            title: '',
            description: '',
            breadcrumb: [],
        },

    },
];

const getBreadcrumbSchema = (breadcrumb: Breadcrumb[]) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumb.map(b => ({
        "@type": "ListItem",
        position: b.position,
        name: b.name,
        item: b.item,
    })),
});

const getCanonicalUrl = (path: string) =>
    path === '/' ? `${baseUrl}/` : `${baseUrl}${path}/`;

const getPageRoute = ({ path, element, meta }: PageConfig): RouteObject => ({
    path,
    element: (
        <>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <link rel="canonical" href={getCanonicalUrl(path)} />
                <script type="application/ld+json">
                    {JSON.stringify(getBreadcrumbSchema(meta.breadcrumb))}
                </script>
            </Helmet>
            {element}
        </>
    ),
});

export const routesConfig: RouteObject[] = [
    {
        path: '/',
        element: (
            <>
                <Helmet>
                    <link rel="canonical" href={getCanonicalUrl('/')} />
                </Helmet>
                <ScrollToTop />
                <Root />
            </>
        ),
        errorElement: (
            <>
                <Helmet>
                    <title>Oops! Something went wrong</title>
                    <meta name="description" content="Oops! Something went wrong." />
                    <link rel="canonical" href={`${baseUrl}/error`} />
                </Helmet>
                <ErrorPage />
            </>
        ),
        children: pages.map(getPageRoute),
    },
];
